import { motion } from "framer-motion";
import React, { JSX, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useIsMobile } from "@utils/hooks";
import { Z } from "@ui/styles/zIndex";
import { NextButton } from "@ui/components/buttons/nextButton";
import { ArrowIcon } from "@ui/components/icons";
import { row, stack } from "@ui/components/layout";

interface GalleryProps<T> extends React.HTMLAttributes<HTMLDivElement> {
	items: T[];
	renderItem: (item: T, index: number) => JSX.Element;
	initialIndex?: number;
	onClose: () => void;
	loop?: boolean;
	header?: JSX.Element;
}
export function Gallery<T>({
	items,
	renderItem,
	initialIndex,
	onClose,
	loop = true,
	header,
}: GalleryProps<T>): JSX.Element | null {
	const [currentIndex, setCurrentIndex] = useState(initialIndex ?? 0);
	const [direction, setDirection] = useState<"left" | "right" | null>(null);
	const canSnapLeft = currentIndex > 0 || loop;
	const canSnapRight = currentIndex < items.length - 1 || loop;
	const lastScroll = useRef(0);
	const isMobile = useIsMobile();

	const snap = (dir: "left" | "right"): void => {
		setDirection(dir);
		if (dir === "left") {
			setCurrentIndex((prev) => (prev < 1 ? items.length - 1 : prev - 1));
		} else {
			setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
		}
	};

	useEffect(() => {
		const handleKey = (e: KeyboardEvent): void => {
			if (e.key === "Escape") {
				onClose();
			} else if (e.key === "ArrowLeft" && canSnapLeft) {
				snap("left");
			} else if (e.key === "ArrowRight" && canSnapRight) {
				snap("right");
			}
		};
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [canSnapLeft, canSnapRight]);

	useEffect(() => {
		const listener = (): void => {
			const off = window.scrollY;

			if (Math.abs(off - lastScroll.current) > 50) {
				onClose();
			}
		};
		lastScroll.current = window.scrollY;
		document.addEventListener("scroll", listener);
		return () => document.removeEventListener("scroll", listener);
	}, []);

	return (
		<Container>
			<Backdrop
				header={!!header}
				initial={{ backgroundColor: "rgba(0, 0, 0, 0)" }}
				animate={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
				exit={{ backgroundColor: "rgba(0, 0, 0, 0)" }}
			/>
			<BackContainer header={!!header} onClick={onClose}>
				{isMobile && items.length > 1 ? (
					<Swiper
						modules={[Navigation]}
						centeredSlides
						autoHeight
						onSlideChange={(swiper) => setCurrentIndex(loop ? swiper.realIndex : swiper.activeIndex)}
						spaceBetween={5}
						slidesPerView={1}
						initialSlide={currentIndex}
						loop={loop}
						navigation={{
							prevEl: ".swiper-prev",
							nextEl: ".swiper-next",
						}}
					>
						{items.map((item, index) => (
							<SwiperSlide key={index}>
								<CenteredItem header={!!header}>{renderItem(item, index)}</CenteredItem>
							</SwiperSlide>
						))}
					</Swiper>
				) : (
					<motion.div
						key={currentIndex}
						initial={{ x: direction === "right" ? "50vw" : direction === "left" ? "-50vw" : 0, opacity: 0 }}
						animate={{ x: 0, opacity: 1 }}
						transition={{ type: "spring", damping: 10, mass: 0.4 }}
					>
						{renderItem(items[currentIndex], currentIndex)}
					</motion.div>
				)}
			</BackContainer>
			{items.length > 1 ? (
				isMobile ? (
					<>
						<ArrowWrapper dir={"left"} className="swiper-prev" style={{ opacity: +canSnapLeft }}>
							<ArrowIcon />
						</ArrowWrapper>
						<ArrowWrapper dir={"right"} className="swiper-next" style={{ opacity: +canSnapRight }}>
							<ArrowIcon />
						</ArrowWrapper>
					</>
				) : (
					<>
						{canSnapLeft && <NavArrow dir={"left"} onClick={() => snap("left")} />}
						{canSnapRight && <NavArrow dir={"right"} onClick={() => snap("right")} />}
					</>
				)
			) : null}
			{!!header && <DarkerHeader>{header}</DarkerHeader>}
		</Container>
	);
}

const absoluteStyle = css`
	position: fixed;
	z-index: ${Z.modal};
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	margin: 0 !important;
`;

const Container = styled.div``;

const CenteredItem = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "header",
})<{ header?: boolean }>`
	${stack(0, "center", "center")};
	height: 100vh;
	top: ${({ header }) => (header ? 50 : 0)}px;
`;

const Backdrop = styled(motion.div)<{ header?: boolean }>`
	${absoluteStyle};
	top: ${({ header }) => (header ? 50 : 0)}px;
`;

const BackContainer = styled(motion.div)<{ header?: boolean }>`
	${absoluteStyle};
	cursor: zoom-out;
	user-select: none;
	top: ${({ header }) => (header ? 50 : 0)}px;
	${row(0, "center", "center")};

	> * {
		width: 95vw;
		height: 95vh;
		${row(0, "center", "center")};
	}
`;

const navArrowStyle = css<{ dir: "left" | "right" }>`
	z-index: ${Z.modal};
	position: fixed;
	${({ dir }) => `${dir}: 20px`};
	top: 50%;
	transform: translateY(-50%) ${({ dir }) => (dir === "right" ? "scaleX(-1)" : "")};
	background-color: ${({ theme }) => theme.colors.white};
	svg path {
		fill: ${({ theme }) => theme.colors.secondary};
	}
`;

const ArrowWrapper = styled.div<{ dir: "left" | "right" }>`
	${navArrowStyle};
	padding: 12px;
	border-radius: 24px;
`;

const NavArrow = styled(NextButton)<{ dir: "left" | "right" }>`
	${navArrowStyle};
	&:hover {
		background-color: ${({ theme }) => theme.colors.secondaryLight};
	}
`;

const DarkerHeader = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	background-color: #1a1919;
	z-index: 10;
	min-height: 50px;
	max-height: 50px;
	height: 50px;
	padding-right: 20px;
	padding-left: 20px;
	font-family: Sora;
	font-style: normal;
	font-weight: 700;
	font-size: 18px;
	line-height: 1;
	color: #ffffff;
`;
